import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Select,
    Input,
    DateInput,
    MultiSelect
} from '../../../Common';
import '../../Table/Table.css';
import { TranslateW } from '../../../../Utils/Languages/Translate';
import { Box } from '@mui/material';

const RangeFilter = ({ state, setState, handleKeyDown, initialValue = '0-0', filterProps = {} }) => {
    
    const [start, end] = initialValue.split('-');

    const handleStartRangeChange = (e) => {
        setState({ startRange: e.target.value });
    };

    const handleEndRangeChange = (e) => {
        setState({ endRange: e.target.value });
    };

    const handleBlur = (key) => {
        if (key === "startRange"  && state.startRange === "") {
            return setState({ startRange: start });
        }
        
        if (key === "endRange"  && state.endRange === "") {
            return setState({ endRange: end });
        }
    };

    return (
        <Box sx={filterProps.style}>
            <Input
                fullWidth
                type="number"
                value={state.startRange}
                label={filterProps.startRangeText || "Greater Than"}
                variant="outlined"
                onBlur={() => handleBlur("startRange")}
                onChange={handleStartRangeChange}
                onKeyDown={handleKeyDown}
                sx={{ marginBottom: "10px" }}
            />
            <Input
                fullWidth
                disabled={filterProps.endRangeDisable}
                type="number"
                value={state.endRange}
                label={filterProps.endRangeText || "Less Than"}
                variant="outlined"
                onBlur={() => handleBlur("endRange")}
                onChange={handleEndRangeChange}
                onKeyDown={handleKeyDown}
            />
        </Box>
    );
};

const DateFilter = ({ state, filterProps, disableDatePickerPortal, setDates }) => (
    <div className="createFilter-date">
        <DateInput
            allowPast
            label="Start Date"
            date={state.startDate}
            open={state.startDateOpen}
            maxDate={!filterProps.dateAllowFuture ? new Date() : (filterProps.startDateMax || state.endDate) }
            allowFuture={filterProps.dateAllowFuture}
            onChange={date => setDates(date, 'startDate')}
            width={50}
            sx={{ margin: '4% 2.5% 0%' }}
            disableDatePickerPortal={disableDatePickerPortal}
        />
        <DateInput
            allowPast
            label="End Date"
            date={state.endDate}
            open={state.endDateOpen}
            minDate={state.startDate}
            maxDate={!filterProps.dateAllowFuture ? new Date() : (filterProps.endDateMax || null) }
            allowFuture={filterProps.dateAllowFuture}
            onChange={date => setDates(date, 'endDate')}
            width={50}
            sx={{ margin: '4% 2.5% 0%' }}
            disableDatePickerPortal={disableDatePickerPortal}
        />
    </div>
);

const CreateFilter = ({
    type,
    filterValues,
    applyFunction,
    filterProps,
    disableDatePickerPortal,
    name,
    isFilterObject,
    initialValue
}) => {

    const [filterType, setFilterType] = useState(null);

    const [state, setNewState] = useState({
        filterText: null,
        filterText2: null,
        startDate: null,
        startDateOpen: true,
        endDate: null,
        endDateOpen: false,
        startRange: '',
        endRange: '',
        multiselect: [],
    });

    const setState = (new_state_obj) => {
        setNewState({
            ...state,
            ...new_state_obj
        })
    }

    const setDates = (date, typeOfDate) => {
        if (typeOfDate === 'startDate') {
            return setState({
                startDate: date,
                startDateOpen: false,
                endDateOpen: true,
            });
        }
        return setState({
            endDate: date,
            endDateOpen: false,
        });
    };

    const handleKeyDown = (e) => {
        e.stopPropagation();
        if (e.keyCode === 13) {
            return applyFunction(state.filterText || state.filterText2);
        }
    };

    useEffect(() => {
        setFilterType(type);
    }, [type]);

    useEffect(() => {
        if (initialValue && type) {
            const newState = { ...state };
            switch (type) {
                case 'range':
                    if (typeof initialValue === 'string') {
                        const [start, end] = initialValue.split('-').map(Number);
                        newState.startRange = start;
                        newState.endRange = end;
                    }
                    break;

                case 'text':
                    newState.filterText = initialValue || '';
                    break;

                case 'date':
                    if (typeof initialValue === 'string') {
                        const [startDate, endDate] = initialValue.split('-').map(date => date.trim());
                        newState.startDate = startDate;
                        newState.endDate = endDate;
                    }
                    break;

                case 'multiselect':
                    newState.multiselect = Array.isArray(initialValue) ? initialValue : [];
                    break;
                case 'dropdown':
                    newState.dropdownValue = initialValue || null;
                    break;

                default:
                    break;
            }

            setNewState(newState);
        }
    }, [initialValue, type]);

    //Combine date start and end to a single filter string
    useEffect(() => {
        const { startDate, endDate } = state;
        let startText = '';
        let endText = '';

        if (startDate) {
            startText = moment(startDate).format("L");
        }
        if (endDate) {
            endText = moment(endDate).format("L");
        }
        if ((startDate != null && startDate != '') || (endDate != null && endDate != '')) {
            setState({ filterText: `${startText}-${endText}` });
        }

    }, [state.startDate, state.endDate]);

    //Combining number range to single string
    useEffect(() => {
        const { startRange, endRange } = state;
        if ((startRange != null && startRange != '') || (endRange != null && endRange != '')) {
            setState({
                filterText: `${startRange}-${endRange}`
            });
        }

    }, [state.startRange, state.endRange]);

    const MultiSelectFilter = ({filterProps = {} }) => (
        <div>
            <MultiSelect
                value={state.multiselect}
                label={name}
                options={filterValues}
                onChange={e => {
                    const {
                        target: { value },
                    } = e;
                    setState({ multiselect: typeof value === 'string' ? value.split(',') : value });
                }}
                isFilterObject={isFilterObject}
                disabledMenuItems={filterProps.disabledMenuItems}
            />
        </div>
    );

    const DropDownFilter = () => (
        <Select
            options={filterValues}
            value={state.filterText}
            label="Search"
            onChange={e => {
                setState({ filterText: e.target.value });
            }}
            isFilterObject={isFilterObject}
        />
    );

    const ApplyButton = () => (
        <div className="createFilter-apply">
            <div className="createFilter-apply-text" onClick={() => applyFunction(state.filterText || state.filterText2 || (state.multiselect.length > 0 ? state.multiselect : null))}>
                {TranslateW("columnCustomizationApplyButton")}
            </div>
        </div>
    );

    return (
        <>
            {filterType === 'multiselect' && <MultiSelectFilter filterProps={filterProps} />}

            {filterType === 'dropdown' && <DropDownFilter />}

            {(filterType === 'text' || filterType === 'number') && (
                <Input
                    fullWidth
                    type= {filterType === 'number' ? "number" : null}
                    value={state.filterText}
                    label="Search"
                    variant="outlined"
                    onChange={e => setState({ filterText: e.target.value })}
                    onKeyDown={e => handleKeyDown(e)}
                />
            )}

            {
                filterType === "textWithDropdown" && (
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignContent: "center",
                        gap: "10px"
                    }}>
                        <DropDownFilter />
                        <Input
                            fullWidth
                            value={state.filterText2}
                            label="Search"
                            variant="outlined"
                            onChange={e => setState({ filterText2: e.target.value })}
                            onKeyDown={e => handleKeyDown(e)}
                        />
                    </div>
                )
            }

            {filterType === 'date' && <DateFilter state={state} filterProps={filterProps} disableDatePickerPortal={disableDatePickerPortal} setDates={setDates} />}

            {filterType === 'range' && <RangeFilter state={state} setState={setState} handleKeyDown={handleKeyDown} filterProps={filterProps} initialValue={initialValue} />}

            <ApplyButton />
        </>
    );
};

CreateFilter.propTypes = {
    type: PropTypes.string.isRequired,
    filterValues: PropTypes.array.isRequired,
    applyFunction: PropTypes.func.isRequired,
    filterProps: PropTypes.object,
};

CreateFilter.defaultProps = {
    filterProps: { dateAllowFuture: true },
    name: "",
    isFilterObject: false
};

export default CreateFilter;
